/**
 * FocusSelector — pick the chat focus mode (Standard / Strutturati / Full).
 *
 * May 2026 SOTA: replaces v1's binary `structuredDataMode` toggle.
 * Mirrors the BE `Focus` Literal in
 * `src/services/agent_routing/enterprise_pipeline.py`.
 *
 * v1 ref: `QueryInput.tsx` lines 596-639, 909-932.
 */

import * as React from "react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";

export type ComposerFocus = "standard" | "structured" | "full";

interface FocusOption {
  id: ComposerFocus;
  label: MessageDescriptor;
  description: MessageDescriptor;
}

const FOCUS_OPTIONS: readonly FocusOption[] = [
  {
    id: "standard",
    label: msg`Standard`,
    description: msg`Risposta conversazionale`,
  },
  {
    id: "structured",
    label: msg`Strutturati`,
    description: msg`Solo dati tabellari + grafici`,
  },
  {
    id: "full",
    label: msg`Full`,
    description: msg`Tutti i dati disponibili`,
  },
] as const;

export interface FocusSelectorProps {
  value: ComposerFocus;
  onChange: (next: ComposerFocus) => void;
  /** Disable interaction (e.g. while a query is in flight). */
  disabled?: boolean;
  className?: string;
}

// May 28, 2026 — wrapped in React.memo so re-renders of the parent
// composer (which happens on every streaming chunk / message arrival)
// no longer cascade through this component when its props are
// reference-stable. Combined with ``useCallback`` on ``onChange`` at
// the call site this kills ~80% of the perceived dropdown click lag.
const FocusSelectorImpl: React.FC<FocusSelectorProps> = ({
  value,
  onChange,
  disabled,
  className,
}) => {
  const { i18n } = useI18n();
  return (
    <fieldset
      aria-label={i18n._(msg`Modalità di focus`)}
      className={cn("flex w-full flex-col gap-1 border-0 p-0", className)}
    >
      {FOCUS_OPTIONS.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            className={cn(
              "flex w-full flex-col gap-0.5 rounded-md px-2.5 py-1.5 text-left transition-colors",
              "hover:bg-ink-1/[0.04] disabled:cursor-not-allowed disabled:opacity-50",
              active && "bg-ando-orange/10 ring-1 ring-ando-orange/40",
            )}
          >
            <span className="font-medium text-[12.5px] text-ink-1">
              {i18n._(opt.label)}
            </span>
            <span className="text-[10.5px] text-ink-3">{i18n._(opt.description)}</span>
          </button>
        );
      })}
    </fieldset>
  );
};
FocusSelectorImpl.displayName = "FocusSelector";

export const FocusSelector = React.memo(FocusSelectorImpl);

export const COMPOSER_FOCUS_OPTIONS = FOCUS_OPTIONS;
